// CommandSearch — поиск по всем командам, как Toolbox в Fusion 360 (клавиша S).
//
// Зачем: команд много, они разложены по вкладкам и выпадающим спискам, и
// помнить, где лежит нужная, невозможно. В Fusion достаточно нажать S и
// набрать пару букв — это заметно быстрее, чем искать иконку глазами.
//
// Поиск нечёткий по подстроке, плюс латиница→кириллица: «extrude» находит
// «Выдавить», потому что у команд есть английские идентификаторы.
import React, { useState, useMemo, useEffect, useRef } from 'react';

export default function CommandSearch({ commands, onRun, onClose }) {
    const [query, setQuery] = useState('');
    const [cursor, setCursor] = useState(0);
    const inputRef = useRef(null);

    useEffect(() => { inputRef.current?.focus(); }, []);

    const found = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) {
            // Пустой запрос — показываем ходовые команды, а не пустоту.
            return commands.filter((c) => !c.disabled).slice(0, 10);
        }
        const scored = [];
        for (const c of commands) {
            const label = String(c.label || '').toLowerCase();
            const id = String(c.id || '').toLowerCase();
            const group = String(c.groupLabel || '').toLowerCase();
            let score = -1;
            // Совпадение с начала названия — лучший результат.
            if (label.startsWith(q)) score = 0;
            else if (label.includes(q)) score = 1;
            else if (id.startsWith(q)) score = 2;
            else if (id.includes(q)) score = 3;
            /*
             * По названию ГРУППЫ ищем только если запрос длинный и ничего
             * не нашлось по самим командам. Иначе запрос «эскиз» выдавал
             * десяток команд из ленты эскиза, и нужная тонула среди них.
             */
            else if (q.length >= 4 && group.startsWith(q)) score = 8;
            if (score >= 0) scored.push({ c, score });
        }
        scored.sort((a, b) => a.score - b.score
            || String(a.c.label).localeCompare(String(b.c.label)));
        return scored.map((x) => x.c).slice(0, 12);
    }, [query, commands]);

    // Курсор не должен уезжать за пределы найденного.
    useEffect(() => { setCursor(0); }, [query]);

    const run = (cmd) => {
        if (!cmd || cmd.disabled) return;
        onClose();
        onRun(cmd);
    };

    const onKeyDown = (e) => {
        if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setCursor((i) => Math.min(i + 1, found.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setCursor((i) => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            run(found[cursor]);
        }
    };

    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 5000,
                background: 'rgba(0,0,0,.35)',
                display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                paddingTop: '14vh',
            }}
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                style={{
                    width: 460, maxWidth: '92vw',
                    background: '#23262e', color: '#e6edf3',
                    borderRadius: 10, overflow: 'hidden',
                    boxShadow: '0 16px 48px rgba(0,0,0,.5)',
                    border: '1px solid rgba(255,255,255,.10)',
                }}
            >
                <input
                    ref={inputRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder="Команда — например «выдавить» или «extrude»"
                    style={{
                        width: '100%', boxSizing: 'border-box',
                        padding: '13px 15px', border: 'none', outline: 'none',
                        background: 'transparent', color: '#e6edf3',
                        font: '15px/1.3 system-ui, sans-serif',
                        borderBottom: '1px solid rgba(255,255,255,.10)',
                    }}
                />
                <div style={{ maxHeight: '52vh', overflowY: 'auto' }}>
                    {found.length === 0 && (
                        <div style={{ padding: '14px 15px', opacity: .6, fontSize: 13 }}>
                            Ничего не найдено
                        </div>
                    )}
                    {found.map((c, i) => (
                        <div
                            key={`${c.groupId}/${c.id}`}
                            onMouseEnter={() => setCursor(i)}
                            onMouseDown={(e) => { e.preventDefault(); run(c); }}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 10,
                                padding: '9px 15px', cursor: 'pointer',
                                background: i === cursor ? 'rgba(0,176,240,.20)' : 'transparent',
                                opacity: c.disabled ? .45 : 1,
                            }}
                        >
                            <span style={{ flex: 1, fontSize: 14 }}>{c.label}</span>
                            <span style={{ fontSize: 11, opacity: .55 }}>{c.groupLabel}</span>
                            {c.disabled && (
                                <span style={{ fontSize: 11, opacity: .55 }}>скоро</span>
                            )}
                        </div>
                    ))}
                </div>
                <div style={{
                    padding: '8px 15px', fontSize: 11, opacity: .5,
                    borderTop: '1px solid rgba(255,255,255,.08)',
                }}>
                    ↑↓ — выбор · Enter — выполнить · Esc — закрыть
                </div>
            </div>
        </div>
    );
}
